import {Injectable} from '@angular/core'
import {
  AgentExpectationOfferValidationType,
  ClientExpectation,
  ExpectedExecution,
  Ownership,
  Salutation,
  StaticJobQuestionsInput,
} from '../../../../__generated__/types'
import {FileModel} from '../../../craft/files/file.model'
import {FormState} from '../../../shared/components/dynamic-form/dynamic-form.model'
import {SessionStorageService} from '../../../core/storage/session-storage.service'
import {StaticFormQuestions} from './job-request-detail-page/static-form/static-form.types'
import {ErrorMonitor} from '../../../core/error/error-monitoring.types'

export const JOB_REQUEST_STATE_DETAIL_STORAGE_KEY = 'job-request-form.details'
export const JOB_REQUEST_STATE_DAMAGE_STORAGE_KEY = 'job-request-form.damage'
export const JOB_REQUEST_STATE_CONTACT_STORAGE_KEY = 'job-request-form.contact'
export const JOB_REQUEST_STATE_BILLING_DETAILS_STORAGE_KEY = 'job-request-form.billing-details'
export const JOB_REQUEST_STATE_TIME_STORAGE_KEY = 'job-request-form.time'
export const JOB_REQUEST_STATE_FILES_STORAGE_KEY = 'job-request-form.files'
export const JOB_REQUEST_STATE_OFFERING_STORAGE_KEY = 'job-request-form.offering'
export const JOB_REQUEST_STATE_DYNAMIC_FORM_STORAGE_KEY = 'job-request-form.dynamic'
export const JOB_REQUEST_STATE_STATIC_FORM_STORAGE_KEY = 'job-request-form.static'
export const JOB_REQUEST_STATE_RECENT_JOB_NUMBER = 'job-request.recent-id'
export const JOB_REQUEST_STATE_STAKEHOLDER = 'job-request.stakeholder'
export const JOB_REQUEST_STATE_INTENTION = 'job-request-form.intention'
export const JOB_REQUEST_STATE_CLIENT_EXPECTATION = 'job-request-form.expectation'
export const JOB_REQUEST_STATE_PREFERRED_PRO = 'job-request.preferred-pro'

@Injectable({
  providedIn: 'root',
})
export class JobRequestRepository {
  constructor(
    private readonly sessionStorage: SessionStorageService,
    private readonly errorMonitor: ErrorMonitor,
  ) {}

  storeJobRequestDetailState(state: JobRequestDetail & AdditionalFormState) {
    this.sessionStorage.setObject(JOB_REQUEST_STATE_DETAIL_STORAGE_KEY, {
      title: state.title,
      description: state.description,
      referrer: state.referrer,
      voucherCode: state.voucherCode,
      addPhotos: state.addPhotos,
      hasVoucher: state.hasVoucher,
    })
  }

  storeJobRequestDamageState(state: JobRequestDamage) {
    this.sessionStorage.setObject(JOB_REQUEST_STATE_DAMAGE_STORAGE_KEY, {
      agentJobRequestCode: state.agentJobRequestCode,
      caseNumber: state.caseNumber,
      occurredAt: state.occurredAt,
      agentDirectives: state.agentDirectives,
    })
  }

  storeJobRequestOfferingState(state: JobRequestOffering) {
    this.sessionStorage.setObject(JOB_REQUEST_STATE_OFFERING_STORAGE_KEY, state)
  }

  storeDynamicFormState(state: FormState) {
    this.sessionStorage.setObject(JOB_REQUEST_STATE_DYNAMIC_FORM_STORAGE_KEY, state)
  }

  storeStaticFormState(state: JobRequestStaticFormState) {
    this.sessionStorage.setObject(JOB_REQUEST_STATE_STATIC_FORM_STORAGE_KEY, state)
  }

  storeJobRequestFilesState(state: JobRequestFiles) {
    if (Array.isArray(state.files)) {
      const toStore = {
        files: state.files
          .filter(f => f.uploaded && f.url) // only store uploaded files
          .map(file => ({...file, data: undefined})), // don't store raw file data, storage would explode
      }
      this.sessionStorage.setObject(JOB_REQUEST_STATE_FILES_STORAGE_KEY, toStore)
    }
  }

  storeJobRequestContactState(state: JobRequestContact) {
    this.sessionStorage.setObject(JOB_REQUEST_STATE_CONTACT_STORAGE_KEY, state)
    this.errorMonitor.setContext('Job request contact', {email: state.email})
  }

  storeJobRequestBillingDetails(state: JobRequestBillingDetails) {
    this.sessionStorage.setObject(JOB_REQUEST_STATE_BILLING_DETAILS_STORAGE_KEY, state)
  }

  storeJobRequestExecutionState(state: JobRequestExecutionDetails) {
    this.sessionStorage.setObject(JOB_REQUEST_STATE_TIME_STORAGE_KEY, state)
  }

  storeEmergencyJobRequestExecutionDate(state: EmergencyJobRequestExecutionDetails) {
    this.sessionStorage.setObject(JOB_REQUEST_STATE_TIME_STORAGE_KEY, state)
  }

  setRecentJobNumber(jobNumber: string): void {
    this.sessionStorage.setObject(JOB_REQUEST_STATE_RECENT_JOB_NUMBER, jobNumber)
  }

  storeStakeholderState(stakeholder: JobRequestStakeholder): void {
    this.sessionStorage.setObject(JOB_REQUEST_STATE_STAKEHOLDER, stakeholder)
  }

  storePreferredProState(pro: JobRequestPreferredPro) {
    this.sessionStorage.setObject(JOB_REQUEST_STATE_PREFERRED_PRO, pro)
  }

  storeClientExpectationState(clientExpectation: ClientExpectation): void {
    this.sessionStorage.setObject(JOB_REQUEST_STATE_CLIENT_EXPECTATION, clientExpectation)
  }

  readJobRequestDetailState(): Partial<JobRequestDetail> & AdditionalFormState {
    const state = this.sessionStorage.getObject(JOB_REQUEST_STATE_DETAIL_STORAGE_KEY) ?? {}
    return state as Partial<JobRequestDetail> & AdditionalFormState
  }

  readJobRequestDamageState(): JobRequestDamage | undefined {
    const state = this.sessionStorage.getObject(JOB_REQUEST_STATE_DAMAGE_STORAGE_KEY)
    return state as JobRequestDamage
  }

  readJobRequestFilesState(): Partial<JobRequestFiles> {
    const state = this.sessionStorage.getObject(JOB_REQUEST_STATE_FILES_STORAGE_KEY) ?? {}
    return state as JobRequestFiles
  }

  readJobRequestContactState(): Partial<JobRequestContact> | undefined {
    return this.sessionStorage.getObject<JobRequestContact>(JOB_REQUEST_STATE_CONTACT_STORAGE_KEY)
  }

  readJobRequestBillingDetailsState(): Partial<JobRequestBillingDetails> | undefined {
    return this.sessionStorage.getObject<JobRequestBillingDetails>(JOB_REQUEST_STATE_BILLING_DETAILS_STORAGE_KEY)
  }

  readJobRequestExecutionState(): Partial<JobRequestExecutionDetails> | undefined {
    return this.sessionStorage.getObject<JobRequestExecutionDetails>(JOB_REQUEST_STATE_TIME_STORAGE_KEY)
  }

  readJobRequestOfferingState(): JobRequestOffering | undefined {
    const state = this.sessionStorage.getObject(JOB_REQUEST_STATE_OFFERING_STORAGE_KEY)
    return state as JobRequestOffering
  }

  readDynamicFormState(): FormState | undefined {
    const state = this.sessionStorage.getObject(JOB_REQUEST_STATE_DYNAMIC_FORM_STORAGE_KEY)
    return state as FormState
  }

  readStaticFormState(): JobRequestStaticFormState | undefined {
    const state = this.sessionStorage.getObject(JOB_REQUEST_STATE_STATIC_FORM_STORAGE_KEY)
    return state as JobRequestStaticFormState
  }

  readClientExpectationState(): ClientExpectation | undefined {
    const state = this.sessionStorage.getObject(JOB_REQUEST_STATE_CLIENT_EXPECTATION)
    return state as ClientExpectation
  }

  getRecentJobNumber(): string | undefined {
    return this.sessionStorage.getObject(JOB_REQUEST_STATE_RECENT_JOB_NUMBER)
  }

  readJobRequestStakeholderState(): JobRequestStakeholder | undefined {
    const state = this.sessionStorage.getObject(JOB_REQUEST_STATE_STAKEHOLDER)
    return state as JobRequestStakeholder
  }

  readJobRequestPreferredProState(): JobRequestPreferredPro | undefined {
    const state = this.sessionStorage.getObject(JOB_REQUEST_STATE_PREFERRED_PRO)
    return state as JobRequestPreferredPro
  }

  /**
   * Clears any state that can't be carried between two distinct request
   */
  clearNonReusableRequestState(): void {
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_DETAIL_STORAGE_KEY)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_FILES_STORAGE_KEY)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_OFFERING_STORAGE_KEY)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_DYNAMIC_FORM_STORAGE_KEY)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_STATIC_FORM_STORAGE_KEY)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_DAMAGE_STORAGE_KEY)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_STAKEHOLDER)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_INTENTION)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_CLIENT_EXPECTATION)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_PREFERRED_PRO)
  }

  /**
   * Clears all state related to a draft job request
   */
  clearJobRequestState(): void {
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_DETAIL_STORAGE_KEY)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_CONTACT_STORAGE_KEY)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_BILLING_DETAILS_STORAGE_KEY)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_TIME_STORAGE_KEY)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_FILES_STORAGE_KEY)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_OFFERING_STORAGE_KEY)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_DYNAMIC_FORM_STORAGE_KEY)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_STATIC_FORM_STORAGE_KEY)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_DAMAGE_STORAGE_KEY)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_STAKEHOLDER)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_INTENTION)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_CLIENT_EXPECTATION)
    this.sessionStorage.removeItem(JOB_REQUEST_STATE_PREFERRED_PRO)
  }

  getIntentionSlug(): string | undefined {
    return this.sessionStorage.getItem(JOB_REQUEST_STATE_INTENTION)
  }

  setIntentionSlug(slug: string) {
    return this.sessionStorage.setItem(JOB_REQUEST_STATE_INTENTION, slug)
  }
}

export interface JobRequestState {
  detail: JobRequestDetail
  damage?: JobRequestDamage
  contact: JobRequestContact
  billingDetails: JobRequestBillingDetails
  executionDetails: JobRequestExecutionDetails
  attachedFiles: JobRequestFiles
  offering?: JobRequestOffering
  clientExpectation?: ClientExpectation
  damageAgentExpectation?: {
    offerValidationType: AgentExpectationOfferValidationType
    deductible?: number
    coverage?: number
  }
  stakeholder?: JobRequestStakeholder
  analyticParams?: Record<string, string>
  preferredPro?: JobRequestPreferredPro
}

export interface JobRequestDetail {
  title?: string
  description: string
  referrer?: string
  voucherCode?: string
}

export interface AdditionalFormState {
  addPhotos: boolean
  hasVoucher?: boolean
}

export interface JobRequestDamage {
  agentJobRequestCode: string
  agentDirectives?: string
  caseNumber: string
  occurredAt: string
}

export interface JobRequestOffering {
  id: string
  questions: JobRequestOfferingQuestion[]
  staticQuestions?: StaticJobQuestionsInput
  intentionSlug?: string
}

export interface JobRequestOfferingQuestion {
  id: string
  answerIds?: string[]
  answerValue?: string
  additionalInformation?: string
}

export interface JobRequestContact {
  salutation: Salutation
  firstName: string
  lastName: string
  company?: string
  phoneNumber: string
  email: string
}

export interface JobRequestBillingDetails {
  billingAddressNotIdentical: boolean
  billingAddress?: JobRequestAddress
  billingClient?: JobRequestContact
}

export interface JobRequestExecutionDetails {
  expectedExecution: ExpectedExecution
  latestPossibleDate?: string
  ownership: Ownership
  floor?: string
  executionAddress?: JobRequestAddress
  skipExecutionDate?: boolean
}

export interface EmergencyJobRequestExecutionDetails {
  expectedExecution: ExpectedExecution
  latestPossibleDate: string
  skipExecutionDate: boolean
}

export interface JobRequestAddress {
  postalCode: number
  locality: string
  streetAndHouseNumber: string
}

export interface JobRequestFiles {
  files?: FileModel[]
}

export interface JobRequestStakeholder {
  agencyJobRequestCode: string
}

export interface JobRequestPreferredPro {
  proId: string
}

export interface JobRequestStaticFormState {
  staticForm: StaticFormQuestions
  extra?: unknown
}

export const VoucherAnchorTag = 'voucher-anchor'
export const PhotosAnchorTag = 'photos-anchor'
